import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { callApi, HttpMethod } from "src/services/apiService";
import { toast } from "sonner";
import { TC } from "../utils/trustCallApi";
import { getAttLabel } from "../utils/carrierDisplay";
import { useAccountConfig } from "../hooks/useAccountConfig";

// Shared inline style for action buttons matching Legal Entities pattern
const btnStyle: React.CSSProperties = {
  backgroundColor: "var(--navbar-and-treeview-bg)",
  borderColor: "var(--navbar-and-treeview-bg)",
  color: "#fff",
};

const iconProps = { width: 16, height: 16, fill: "currentColor", className: "me-1", style: { verticalAlign: "middle" } as React.CSSProperties };

const DownloadIcon = () => (
  <svg viewBox="0 0 512 512" {...iconProps}>
    <path d="M368 256 256 384 144 256h96V32h32v224zm112 192H32v32h448z" />
  </svg>
);

const ResetIcon = () => (
  <svg viewBox="0 0 512 512" {...iconProps}>
    <path d="m101.4 238.6 46.4 12.4c-2.5 9.3-3.8 19-3.8 29 0 61.8 50.2 112 112 112s112-50.2 112-112-50.2-112-112-112v56l-128-80 128-80v56c88.4 0 160 71.6 160 160s-71.6 160-160 160S96 368.4 96 280c0-14.3 1.9-28.2 5.4-41.4" />
  </svg>
);

const StopIcon = () => (
  <svg viewBox="0 0 512 512" {...iconProps}>
    <path d="M416 416H96V96h320z" />
  </svg>
);

const RescanIcon = () => (
  <svg viewBox="0 0 512 512" {...iconProps}>
    <path d="M32 96H0V0h96v32H32zm0 288H0v96h96v-32H32zM416 0v32h64v64h32V0zm64 448h-64v32h96v-96h-32zm-192-32h32V288h-32zm32-352h-32v128h32zm-64 0h-32v128h32zM128 416h64V288h-64zm-64 0h32V288H64zM192 64h-64v128h64zm32 352h32V288h-32zM64 64v128h32V64zm384 0h-32v128h32zm0 352V288h-32v128zm-96 0h32V288h-32zm32-352h-32v128h32zm96 160H32v32h448z" />
  </svg>
);

const SysadminTools: React.FC = () => {
  const queryClient = useQueryClient();
  const config = useAccountConfig();
  const isCanadian = config.data?.isCanadian ?? false;

  // Rescan state
  const [scanVerizon, setScanVerizon] = useState(false);
  const [scanATT, setScanATT] = useState(false);
  const [scanTMobile, setScanTMobile] = useState(false);

  // TNS complaint export option
  const [tnsIncludeNotSpam, setTnsIncludeNotSpam] = useState(false);

  // Inventory service types
  const [invServiceTypes, setInvServiceTypes] = useState({
    scanning: false,
    remediation: true,
    dailyScans: true,
    oneTimeScan: false,
    otsRemediation: false,
    branding: false,
  });

  // Remediation status
  const sysadminQuery = useQuery({
    queryKey: ["trustcall", "settings", "sysadmin"],
    queryFn: () => callApi(TC.SETTINGS_SYSADMIN, HttpMethod.GET),
  });

  // The API field is named `pauseRemediation` but its value is actually "is running"
  // (backend AccountAttributes #97 DefaultValue "0" = running). Null/empty => paused.
  const isRunning = sysadminQuery.data?.pauseRemediation ?? false;

  const pauseMutation = useMutation({
    mutationFn: () => callApi(TC.SETTINGS_PAUSE_REMEDIATION, HttpMethod.POST),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trustcall", "settings", "sysadmin"] });
      toast.success("Remediation automation paused");
    },
    onError: () => toast.error("Failed to pause remediation"),
  });

  const resumeMutation = useMutation({
    mutationFn: () => callApi(TC.SETTINGS_RESUME_REMEDIATION, HttpMethod.POST),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trustcall", "settings", "sysadmin"] });
      toast.success("Remediation automation resumed");
    },
    onError: () => toast.error("Failed to resume remediation"),
  });

  const rescanMutation = useMutation({
    mutationFn: () => callApi(TC.CARRIERS_RESCAN_ALL, HttpMethod.POST, { scanATT, scanTMobile, scanVerizon }),
    onSuccess: (result) => {
      toast.success(`${result.queued} phone number(s) queued for rescan`);
      setScanVerizon(false);
      setScanATT(false);
      setScanTMobile(false);
    },
    onError: () => toast.error("Failed to rescan"),
  });

  const resetBillingMutation = useMutation({
    mutationFn: () => callApi(TC.SETTINGS_BILLING_RESET, HttpMethod.POST),
    onSuccess: () => toast.success("Billing counters reset"),
    onError: () => toast.error("Failed to reset billing counters"),
  });

  // Create TrustCall Overrides state
  const [overrideNumbers, setOverrideNumbers] = useState("");
  const [overrideCarriers, setOverrideCarriers] = useState({ Verizon: true, ATT: false, TMobile: false });
  const [overrideDuration, setOverrideDuration] = useState(7);

  const createOverridesMutation = useMutation({
    mutationFn: () => {
      const carriers = Object.entries(overrideCarriers)
        .filter(([, checked]) => checked)
        .map(([key]) => key);
      if (!overrideNumbers.trim()) { toast.warning("Paste phone numbers first."); return Promise.reject(); }
      if (!carriers.length) { toast.warning("Select at least one carrier."); return Promise.reject(); }
      if (overrideDuration < 1 || overrideDuration > 30) { toast.warning("Duration must be 1-30 days."); return Promise.reject(); }
      return callApi(TC.SETTINGS_CREATE_OVERRIDES, HttpMethod.POST, {
        phoneNumbersBlob: overrideNumbers,
        carriers,
        durationDays: overrideDuration,
      });
    },
    onSuccess: (r: any) => {
      let msg = `Created ${r.inserted}, extended ${r.extended} across ${r.carriers.join(", ")} for ${r.durationDays} days`;
      if (r.invalid?.length) msg += ` (${r.invalid.length} invalid skipped)`;
      toast.success(msg);
      setOverrideNumbers("");
    },
    onError: (err: any) => {
      if (err?.message) toast.error(`Failed to create overrides: ${err.message}`);
    },
  });

  const apiUrl = import.meta.env.VITE_APP_API_URL;

  const handleDownload = (endpoint: string, params?: string) => {
    const url = params ? `${apiUrl}/${endpoint}?${params}` : `${apiUrl}/${endpoint}`;
    window.open(url, "_blank");
  };

  const getInventoryServiceTypes = (): string => {
    const types: string[] = [];
    if (invServiceTypes.scanning) types.push("1");
    if (invServiceTypes.remediation) types.push("2");
    if (invServiceTypes.dailyScans) types.push("3");
    if (invServiceTypes.oneTimeScan) types.push("4");
    if (invServiceTypes.otsRemediation) types.push("6");
    if (invServiceTypes.branding) types.push("5");
    return types.join(",");
  };

  return (
    <div>
      <div className="row">
        {/* ===== Left Column ===== */}
        <div className="col-md-6">
          {/* Rescan Flagged Numbers */}
          <div className="card mb-3">
            <div className="card-header tc-card-header">
              <h5 className="mb-0" style={{ color: "white" }}>Rescan Flagged Numbers</h5>
            </div>
            <div className="card-body">
              <p>
                Force a Priority 1 rescan of all numbers in account for a specific carrier or carriers
                that are currently flagged as spam. This will not force a rescan if a number is at its
                flow-control max meaning it will not:
              </p>
              <ol className="small">
                <li>Get a screenshot for a specific number more than once an hour per carrier.</li>
                <li>Make more than 12 calls to any number across all carriers per 24 hour period.</li>
              </ol>
              <p className="small">
                If a number is at the above limits, the dialer will wait to retry the scan until no
                longer at these limits.
              </p>
              <div className="mb-2">
                <strong>Select carrier(s):</strong>
                <div className="d-flex gap-3 mt-1">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="rescanVerizon" checked={scanVerizon} onChange={(e) => setScanVerizon(e.target.checked)} />
                    <label className="form-check-label" htmlFor="rescanVerizon">Verizon</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="rescanATT" checked={scanATT} onChange={(e) => setScanATT(e.target.checked)} />
                    <label className="form-check-label" htmlFor="rescanATT">{getAttLabel(isCanadian)}</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="rescanTMobile" checked={scanTMobile} onChange={(e) => setScanTMobile(e.target.checked)} />
                    <label className="form-check-label" htmlFor="rescanTMobile">T-Mobile</label>
                  </div>
                </div>
              </div>
              <button
                className="btn"
                style={btnStyle}
                onClick={() => rescanMutation.mutate()}
                disabled={rescanMutation.isPending || (!scanVerizon && !scanATT && !scanTMobile)}
              >
                <RescanIcon />
                <span style={{ verticalAlign: "middle" }}>{rescanMutation.isPending ? "Rescanning..." : "Rescan"}</span>
              </button>
            </div>
          </div>

          {/* Remediation Automation */}
          <div className="card mb-3">
            <div className="card-header tc-card-header">
              <h5 className="mb-0" style={{ color: "white" }}>Remediation Automation</h5>
            </div>
            <div className="card-body">
              <p>Use these buttons to start and stop the remediation automation</p>
              <p>
                Remediation Automation: <strong>{isRunning ? "Running" : "Stopped"}</strong>
              </p>
              {isRunning ? (
                <button
                  className="btn"
                  style={btnStyle}
                  onClick={() => pauseMutation.mutate()}
                  disabled={pauseMutation.isPending}
                >
                  <StopIcon />
                  <span style={{ verticalAlign: "middle" }}>{pauseMutation.isPending ? "Stopping..." : "Stop"}</span>
                </button>
              ) : (
                <button
                  className="btn"
                  style={btnStyle}
                  onClick={() => resumeMutation.mutate()}
                  disabled={resumeMutation.isPending}
                >
                  <RescanIcon />
                  <span style={{ verticalAlign: "middle" }}>{resumeMutation.isPending ? "Starting..." : "Start"}</span>
                </button>
              )}
            </div>
          </div>

          {/* Download TNS Call Volume */}
          <div className="card mb-3">
            <div className="card-header tc-card-header">
              <h5 className="mb-0" style={{ color: "white" }}>Download TNS Call Volume</h5>
            </div>
            <div className="card-body">
              <p>Download daily call volume data from TNS for all phone numbers currently active in your account.</p>
              <button
                className="btn"
                style={btnStyle}
                onClick={() => handleDownload(TC.EXPORT_TNS_VOLUME)}
              >
                <DownloadIcon />
                <span style={{ verticalAlign: "middle" }}>Download CSV</span>
              </button>
              <span className="text-muted ms-2 small">Downloading will take several minutes</span>
            </div>
          </div>

          {/* Download TNS Complaint Data */}
          <div className="card mb-3">
            <div className="card-header tc-card-header">
              <h5 className="mb-0" style={{ color: "white" }}>Download TNS Complaint Data</h5>
            </div>
            <div className="card-body">
              <p>
                Download TNS crowd source complaint events (spam, not-spam, scam-fraud) for all phone
                numbers currently active in your account.
              </p>
              <div className="form-check mb-1">
                <input className="form-check-input" type="radio" name="tnsNotSpam" id="tnsExclude" checked={!tnsIncludeNotSpam} onChange={() => setTnsIncludeNotSpam(false)} />
                <label className="form-check-label" htmlFor="tnsExclude">Exclude not-spam reports</label>
              </div>
              <div className="form-check mb-3">
                <input className="form-check-input" type="radio" name="tnsNotSpam" id="tnsInclude" checked={tnsIncludeNotSpam} onChange={() => setTnsIncludeNotSpam(true)} />
                <label className="form-check-label" htmlFor="tnsInclude">Include not-spam reports</label>
              </div>
              <button
                className="btn mt-2"
                style={btnStyle}
                onClick={() => handleDownload(TC.EXPORT_TNS_COMPLAINT, `includeNotSpam=${tnsIncludeNotSpam}`)}
              >
                <DownloadIcon />
                <span style={{ verticalAlign: "middle" }}>Download CSV</span>
              </button>
              <small className="text-muted ms-2">Downloading will take several minutes</small>
            </div>
          </div>
        </div>

        {/* ===== Right Column ===== */}
        <div className="col-md-6">
          {/* Reset Billing Counters */}
          <div className="card mb-3">
            <div className="card-header tc-card-header">
              <h5 className="mb-0" style={{ color: "white" }}>Reset Billing Counters to Active Numbers</h5>
            </div>
            <div className="card-body">
              <p>
                Reset the account's billing count of unique phone number this month to the current
                number of phone number that are active in the account. Should be used only in special
                circumstances.
              </p>
              <button
                className="btn"
                style={btnStyle}
                onClick={() => {
                  if (window.confirm("Are you sure you want to reset billing counters?")) {
                    resetBillingMutation.mutate();
                  }
                }}
                disabled={resetBillingMutation.isPending}
              >
                <ResetIcon />
                <span style={{ verticalAlign: "middle" }}>{resetBillingMutation.isPending ? "Resetting..." : "Reset"}</span>
              </button>
            </div>
          </div>

          {/* Create TrustCall Overrides — matching MVC Monitor.cshtml sysadmin section */}
          <div className="card mb-3">
            <div className="card-header tc-card-header">
              <h5 className="mb-0" style={{ color: "white" }}>Create TrustCall Overrides</h5>
            </div>
            <div className="card-body">
              <p>
                Bypass dialer scans for specific numbers. While an override is active, the dialer
                skips the carrier call and returns a clean simulated screenshot. Use for emergency
                client requests.
              </p>
              <label className="form-label"><strong>Phone numbers</strong> (one per line, any format)</label>
              <textarea
                className="form-control"
                rows={6}
                placeholder={"8336642673\n855-715-1660\n..."}
                value={overrideNumbers}
                onChange={(e) => setOverrideNumbers(e.target.value)}
              />
              <div className="mt-2">
                <strong>Carriers:</strong>
                <div className="d-flex flex-wrap gap-3 mt-1">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="overrideVerizon"
                      checked={overrideCarriers.Verizon}
                      onChange={(e) => setOverrideCarriers({ ...overrideCarriers, Verizon: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="overrideVerizon">Verizon</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="overrideATT"
                      checked={overrideCarriers.ATT}
                      onChange={(e) => setOverrideCarriers({ ...overrideCarriers, ATT: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="overrideATT">{getAttLabel(isCanadian)}</label>
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="overrideTMobile"
                      checked={overrideCarriers.TMobile}
                      onChange={(e) => setOverrideCarriers({ ...overrideCarriers, TMobile: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="overrideTMobile">TMobile</label>
                  </div>
                </div>
              </div>
              <div className="mt-2" style={{ maxWidth: 200 }}>
                <label className="form-label"><strong>Duration (days)</strong></label>
                <input
                  type="number"
                  className="form-control"
                  value={overrideDuration}
                  min={1}
                  max={30}
                  onChange={(e) => setOverrideDuration(Number(e.target.value))}
                />
              </div>
              <div className="d-flex align-items-center mt-3">
                <button
                  className="btn"
                  style={btnStyle}
                  onClick={() => createOverridesMutation.mutate()}
                  disabled={createOverridesMutation.isPending}
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}>
                    <path d="M11.67 7.448a.6.6 0 0 1 .755.078l3.24 3.24a.6.6 0 1 1-.849.848L12 8.798l-2.816 2.816a.6.6 0 0 1-.848-.848l3.24-3.24z" />
                    <path d="M11.4 16.05v-8.1a.6.6 0 0 1 1.2 0v8.1a.6.6 0 0 1-1.2 0" />
                    <path d="m20.482 3.945-.001 2.02a.596.596 0 0 1-.6.6h-1.948a.6.6 0 1 1 0-1.201h.526a9.22 9.22 0 0 0-8.496-1.99l-.433.128a9.115 9.115 0 0 0-6.016 11.041 9.123 9.123 0 0 0 11.17 6.449l.317-.091a9.22 9.22 0 0 0 6.386-7.216.6.6 0 0 1 1.182.208 10.43 10.43 0 0 1-7.574 8.258l-.498.121a10.324 10.324 0 0 1-12.142-7.418 10.32 10.32 0 0 1 1.031-7.832A10.32 10.32 0 0 1 9.162 2.36l.491-.145a10.41 10.41 0 0 1 9.628 2.275v-.545a.6.6 0 0 1 1.201 0" />
                  </svg>
                  <span style={{ verticalAlign: "middle" }}>
                    {createOverridesMutation.isPending ? "Creating..." : "Create Overrides"}
                  </span>
                </button>
                <small className="text-muted ms-2">Re-submitting extends existing overrides. Max 5,000 numbers per submit.</small>
              </div>
            </div>
          </div>

          {/* Download TrustCall Inventory */}
          <div className="card mb-3">
            <div className="card-header tc-card-header">
              <h5 className="mb-0" style={{ color: "white" }}>Download TrustCall Inventory</h5>
            </div>
            <div className="card-body">
              <p>
                Download all active TrustCall phone numbers across all accounts with TNRM registration
                status. Select which service types to include:
              </p>
              {[
                { key: "scanning", label: "Scanning" },
                { key: "remediation", label: "Remediation" },
                { key: "dailyScans", label: "Remediation Daily Scans" },
                { key: "oneTimeScan", label: "One Time Scan" },
                { key: "otsRemediation", label: "OTS Remediation" },
                { key: "branding", label: "Branding" },
              ].map(({ key, label }) => (
                <div className="form-check" key={key}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`inv-${key}`}
                    checked={invServiceTypes[key as keyof typeof invServiceTypes]}
                    onChange={(e) => setInvServiceTypes({ ...invServiceTypes, [key]: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor={`inv-${key}`}>{label}</label>
                </div>
              ))}
              <button
                className="btn mt-2"
                style={btnStyle}
                onClick={() => handleDownload(TC.EXPORT_INVENTORY, `serviceTypes=${getInventoryServiceTypes()}`)}
              >
                <DownloadIcon />
                <span style={{ verticalAlign: "middle" }}>Download CSV</span>
              </button>
            </div>
          </div>

          {/* Download All Phone Numbers Score History */}
          <div className="card mb-3">
            <div className="card-header tc-card-header">
              <h5 className="mb-0" style={{ color: "white" }}>Download All Phone Numbers Score History</h5>
            </div>
            <div className="card-body">
              <p>
                Download the daily score history (last 30 days) for all phone numbers currently active
                in your account. Shows per-day spam/clean status by carrier and app.
              </p>
              <button
                className="btn"
                style={btnStyle}
                onClick={() => handleDownload(TC.EXPORT_SCORES_HISTORY)}
              >
                <DownloadIcon />
                <span style={{ verticalAlign: "middle" }}>Download CSV</span>
              </button>
              <span className="text-muted ms-2 small">May take several minutes for large accounts</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SysadminTools;
